// src/components/SyncStatusBadge.jsx
import { CheckCircle, AlertCircle, Clock, RefreshCw } from 'lucide-react';
import styles from './SyncStatusBadge.module.css';

const CONFIG = {
  synced:      { icon: CheckCircle, mod: styles.synced,      label: 'Synced',        compactLabel: '✓', spinning: false },
  syncing:     { icon: RefreshCw,   mod: styles.syncing,     label: 'Syncing…',      compactLabel: '↻', spinning: true  },
  failed:      { icon: AlertCircle, mod: styles.failed,      label: 'Sync failed',   compactLabel: '✗', spinning: false },
  never_synced:{ icon: Clock,       mod: styles.neverSynced, label: 'Never synced',  compactLabel: '—', spinning: false },
};

// Traffic light freshness levels keyed to last_synced_at (per VEN-320 SLA)
// green < 5 min, amber 5–15 min, red > 15 min
function getFreshnessLevel(lastSyncedAt) {
  if (!lastSyncedAt) return null;
  const mins = (Date.now() - new Date(lastSyncedAt).getTime()) / 60000;
  if (mins < 5)  return 'green';
  if (mins <= 15) return 'amber';
  return 'red';
}

const FRESHNESS_MOD = {
  green: styles.freshnessGreen,
  amber: styles.freshnessAmber,
  red:   styles.freshnessRed,
};

function getTimeAgo(timestamp) {
  if (!timestamp) return null;
  const diffMs   = Date.now() - new Date(timestamp);
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays  = Math.floor(diffMs / 86400000);
  if (diffMins < 1)  return 'just now';
  if (diffMins === 1) return '1 min ago';
  if (diffMins < 60) return `${diffMins} mins ago`;
  if (diffHours === 1) return '1 hour ago';
  if (diffHours < 24) return `${diffHours} hours ago`;
  if (diffDays === 1)  return '1 day ago';
  return `${diffDays} days ago`;
}

export default function SyncStatusBadge({ status = 'never_synced', lastSyncedAt, error, compact = false }) {
  const config = CONFIG[status] ?? CONFIG.never_synced;
  const Icon = config.icon;
  const label = (status === 'synced' && lastSyncedAt)
    ? `Synced ${getTimeAgo(lastSyncedAt)}`
    : config.label;

  const freshness = status === 'synced' ? getFreshnessLevel(lastSyncedAt) : null;
  const freshnessMod = freshness ? FRESHNESS_MOD[freshness] : null;

  if (compact) {
    return (
      <span className={`${styles.compact} ${freshnessMod ?? config.mod}`} title={label}>
        {config.compactLabel}
      </span>
    );
  }

  return (
    <div className={styles.wrapper}>
      <div className={`${styles.badge} ${freshnessMod ?? config.mod}`}>
        <Icon
          size={16}
          aria-hidden="true"
          className={config.spinning ? styles.spinning : undefined}
        />
        <span>{label}</span>
        {freshness && <span className={`${styles.freshnessIndicator} ${styles[`freshnessIndicator--${freshness}`]}`} aria-label={`Freshness: ${freshness}`} />}
      </div>
      {status === 'failed' && error && (
        <div className={styles.errorText}>{error}</div>
      )}
    </div>
  );
}
